import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { requestPasswordReset, resetPassword, verifyResetCode } from '../api/auth'
import { useTurnstile } from '../hooks/useTurnstile'
import TurnstileWidget, { type TurnstileWidgetHandle } from '../components/TurnstileWidget'
import { useAuthNav } from '../hooks/useAuthModal'

type Step = 'email' | 'code' | 'password' | 'success'

export default function ForgotPassword() {
	const { t } = useTranslation()
	const [step, setStep] = useState<Step>('email')
	const [email, setEmail] = useState('')
	const [code, setCode] = useState('')
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirm, setShowConfirm] = useState(false)
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)
	const [touched, setTouched] = useState<Record<string, boolean>>({})
	// Шаг email — всегда (TZ_turnstile_captcha.md, 3.5): виджет виден сразу.
	const [captchaToken, setCaptchaToken] = useState('')
	const captchaWidgetRef = useRef<TurnstileWidgetHandle>(null)
	const { enabled: captchaEnabled } = useTurnstile()
	// В окне поверх страницы возврат ко входу остаётся в окне (useAuthNav)
	const authNav = useAuthNav()

	const isPasswordValid = password.length >= 6
	const isConfirmValid = confirmPassword.length > 0 && password === confirmPassword
	const isConfirmError =
		touched.confirm && confirmPassword.length > 0 && password !== confirmPassword

	const getPasswordState = () => {
		if (!touched.password || !password) return ''
		if (isPasswordValid) return 'valid'
		return ''
	}

	const getConfirmState = () => {
		if (!touched.confirm || !confirmPassword) return ''
		if (isConfirmValid) return 'valid'
		if (isConfirmError) return 'error'
		return ''
	}

	const handleSendCode = async (e: React.FormEvent) => {
		e.preventDefault()
		setError('')

		if (!email) {
			setError(t('auth.error.enterEmail'))
			return
		}

		if (captchaEnabled && !captchaToken) {
			setError(t('auth.captcha.required'))
			return
		}

		setLoading(true)
		try {
			await requestPasswordReset(email, captchaToken || undefined)
			setStep('code')
		} catch (err) {
			setError(err instanceof Error ? err.message : t('auth.error.sendCodeFailed'))
			captchaWidgetRef.current?.reset()
			setCaptchaToken('')
		} finally {
			setLoading(false)
		}
	}

	const handleVerifyCode = async (e: React.FormEvent) => {
		e.preventDefault()
		setError('')
		setLoading(true)
		try {
			await verifyResetCode(email, code)
			setStep('password')
			setTouched({})
		} catch (err) {
			setError(err instanceof Error ? err.message : t('auth.error.wrongCode'))
		} finally {
			setLoading(false)
		}
	}

	const handleResetPassword = async (e: React.FormEvent) => {
		e.preventDefault()
		setError('')
		setTouched({ password: true, confirm: true })

		if (!isPasswordValid) {
			setError(t('auth.error.passwordMin'))
			return
		}

		if (password !== confirmPassword) {
			setError(t('auth.error.passwordMismatch'))
			return
		}

		setLoading(true)
		try {
			await resetPassword(email, code, password)
			setStep('success')
		} catch (err) {
			setError(err instanceof Error ? err.message : t('auth.error.resetFailed'))
		} finally {
			setLoading(false)
		}
	}

	if (step === 'success') {
		return (
			<div className="auth-page">
				<div className="auth-card-frame">
					<div className="auth-card">
						<h1 className="auth-title">{t('auth.forgot.success')}</h1>
						<button className="auth-btn auth-btn-primary" onClick={() => authNav.go('/login')}>
							{t('header.login')}
						</button>
					</div>
				</div>
			</div>
		)
	}

	if (step === 'password') {
		const passwordState = getPasswordState()
		const confirmState = getConfirmState()

		return (
			<div className="auth-page">
				<div className="auth-card-frame">
					<div className="auth-card">
						<h1 className="auth-title">{t('auth.forgot.newPassword')}</h1>
						<p className="auth-subtitle">{t('auth.forgot.createNew')}</p>

						{error && <div className="auth-error">{error}</div>}

						<form onSubmit={handleResetPassword} className="auth-form" noValidate>
							<div className="auth-input-wrapper">
								<input
									type={showPassword ? 'text' : 'password'}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									onBlur={() => setTouched((t) => ({ ...t, password: true }))}
									placeholder={t('auth.password')}
									className={`auth-input ${passwordState}`}
								/>
								{password.length > 0 && (
									<button
										type="button"
										className="auth-eye-btn"
										onClick={() => setShowPassword(!showPassword)}
									>
										<img
											src={showPassword ? '/icons/visible.svg' : '/icons/notvisible.svg'}
											alt=""
											width="15"
										/>
									</button>
								)}
							</div>

							<div className="auth-input-wrapper">
								<input
									type={showConfirm ? 'text' : 'password'}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
									placeholder={t('auth.confirmPassword')}
									className={`auth-input ${confirmState}`}
								/>
								{confirmState === 'error' ? (
									<img
										src="/icons/cross.svg"
										className="auth-input-icon"
										alt=""
										width="10"
										height="10"
									/>
								) : confirmPassword.length > 0 ? (
									<button
										type="button"
										className="auth-eye-btn"
										onClick={() => setShowConfirm(!showConfirm)}
									>
										<img
											src={showConfirm ? '/icons/visible.svg' : '/icons/notvisible.svg'}
											alt=""
											width="15"
										/>
									</button>
								) : null}
							</div>
							{isConfirmError && <div className="auth-field-error">{t('auth.error.passwordMismatchField')}</div>}

							<button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
								{loading ? t('auth.forgot.saving') : t('auth.forgot.change')}
							</button>
						</form>
					</div>
				</div>
			</div>
		)
	}

	if (step === 'code') {
		return (
			<div className="auth-page">
				<div className="auth-card-frame">
					<div className="auth-card">
						<h1 className="auth-title">{t('auth.forgot.title')}</h1>
						<p className="auth-subtitle">{t('auth.forgot.enterCode')}</p>

						{error && <div className="auth-error">{error}</div>}

						<form onSubmit={handleVerifyCode} className="auth-form" noValidate>
							<input
								type="text"
								value={code}
								onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
								placeholder={t('auth.forgot.codePlaceholder')}
								className="auth-input"
								inputMode="numeric"
								maxLength={4}
							/>

							<button
								type="submit"
								className="auth-btn auth-btn-primary"
								disabled={loading || code.length !== 4}
							>
								{loading ? t('auth.forgot.verifying') : t('common.next')}
							</button>
						</form>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="auth-page">
			<div className="auth-card-frame">
				<div className="auth-card">
					<h1 className="auth-title">{t('auth.forgot.title')}</h1>
					<p className="auth-subtitle">{t('auth.forgot.enterEmail')}</p>

					{error && <div className="auth-error">{error}</div>}

					<form onSubmit={handleSendCode} className="auth-form" noValidate>
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="E-mail"
							className="auth-input"
						/>

						<TurnstileWidget
							ref={captchaWidgetRef}
							action="password_reset"
							onToken={setCaptchaToken}
							onExpire={() => setCaptchaToken('')}
						/>

						<button
							type="submit"
							className="auth-btn auth-btn-primary"
							disabled={loading || (captchaEnabled && !captchaToken)}
						>
							{loading ? t('auth.forgot.sending') : t('common.next')}
						</button>
					</form>

					<Link to="/login" {...authNav.link} className="auth-link">
						{t('auth.backToLogin')}
					</Link>
				</div>
			</div>
		</div>
	)
}
